import axios, { InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { getCurrentSession, buildLoginRedirectUrl, invalidateSessionCache } from './auth';

const baseURL = process.env.REACT_APP_KCC_API || 'http://localhost:30082/api/kcc';

// 身份由 kcc-service 用 Kratos 会话 cookie 校验；X-Requested-With 供服务端做 CSRF 校验
export const kccApi = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: true,
  headers: { 'X-Requested-With': 'kfy-web' },
});

// 无会话时跳转登录（页面体验，安全校验在服务端）
kccApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    let session = null;
    try {
      session = await getCurrentSession();
    } catch (error) {
      return Promise.reject(error);
    }

    if (!session) {
      window.location.href = buildLoginRedirectUrl();
      return Promise.reject(new Error('Not signed in'));
    }
    return config;
  },
  (error) => Promise.reject(error)
);

kccApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 503) {
      // 服务端回查 Kratos 失败，不跳登录以免循环跳转
      message.error({ content: 'Authentication service unavailable, please try again later', key: 'kcc-auth-unavailable' });
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      let session = null;
      try {
        session = await getCurrentSession();
      } catch {
        return Promise.reject(error);
      }
      if (!session) {
        invalidateSessionCache();
        window.location.href = buildLoginRedirectUrl();
      }
    }
    return Promise.reject(error);
  }
);

export interface TrackingEvent {
  time?: string;
  location?: string;
  description?: string;
  /** 进入本节点时刻 */
  enteredAt?: string | null;
  /** 离开本节点时刻；进行中为 null */
  leftAt?: string | null;
  /** 是否为当前最新节点（停留仍在进行） */
  ongoing?: boolean;
  /** 停留天数（一位小数） */
  dwellDays?: number | null;
}

export interface TrackingDwellSummary {
  segmentCount?: number;
  maxDwellDays?: number | null;
  maxDwellLabel?: string | null;
}

export interface TrackingHistory {
  mode: 'SUMMARY' | 'EVENTS' | string;
  summaryLabel?: string | null;
  events?: TrackingEvent[];
  dwellSummary?: TrackingDwellSummary | null;
}

export interface ValidInvoice {
  invoiceId?: string;
  fileName?: string;
  contentType?: string;
}

export interface ShipmentProjection {
  shipmentId: string;
  clientCode?: string;
  clientOrderId?: string;
  status?: string;
  transportMode?: string;
  /** 运单渠道承运商编码 */
  carrierCode?: string;
  /** 运单渠道承运商名称（无则编码） */
  carrierName?: string;
  channelShipmentId?: string;
  /** 承运商官网轨迹查询链接（海运可为查船链接） */
  carrierTrackingUrl?: string;
  /** 船名（海运） */
  vesselName?: string;
  /** 航次（海运） */
  vesselVoyage?: string;
  clientAddr?: string;
  clientAddrFields?: Record<string, string>;
  cartonCount?: number;
  confirmedWeight?: number | string;
  expectedShipDate?: string;
  trackingHistory?: TrackingHistory;
  validInvoice?: ValidInvoice | null;
  createTimestamp?: number;
  updateTimestamp?: number;
}

export interface PageResult<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface MeResponse {
  userId?: string;
  username?: string;
  customerCode?: string;
  email?: string;
  role?: string;
  systemAccess?: string[];
}

export interface NotificationPreferences {
  shipmentStatusEmailEnabled: boolean;
}

export async function fetchShipments(params: {
  status?: string;
  page?: number;
  size?: number;
}): Promise<PageResult<ShipmentProjection>> {
  const res = await kccApi.get<PageResult<ShipmentProjection>>('/shipments', { params });
  return res.data;
}

export async function fetchShipment(shipmentId: string): Promise<ShipmentProjection> {
  const res = await kccApi.get<ShipmentProjection>(`/shipments/${encodeURIComponent(shipmentId)}`);
  return res.data;
}

export async function fetchMe(): Promise<MeResponse> {
  const res = await kccApi.get<MeResponse>('/me');
  return res.data;
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await kccApi.get<NotificationPreferences>('/notification-preferences');
  return res.data;
}

export async function updateNotificationPreferences(
  body: NotificationPreferences
): Promise<NotificationPreferences> {
  const res = await kccApi.put<NotificationPreferences>('/notification-preferences', body);
  return res.data;
}

export async function downloadInvoice(shipmentId: string, fileName?: string): Promise<void> {
  const res = await kccApi.get(`/shipments/${encodeURIComponent(shipmentId)}/invoice/download`, {
    responseType: 'blob',
  });
  const contentTypeHeader = res.headers['content-type'];
  const contentType =
    typeof contentTypeHeader === 'string' ? contentTypeHeader : 'application/octet-stream';
  const blob = new Blob([res.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'invoice';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
